/**
 * SupportWidget — Floating support button with expandable panel.
 *
 * Two tabs: "Report a Bug" and "Ask Support".
 * Posts to /api/v1/support/bug and /api/v1/support/ask.
 * Dark theme using CSS variables.
 */

import React, { useState } from 'react';
import { apiV1Base } from '../lib/apiConfig';
import {
  X,
  Bug,
  HelpCircle,
  Send,
  Loader2,
  CheckCircle,
} from 'lucide-react';

const BASE_URL =
  apiV1Base();

interface SupportWidgetProps {
  userId?: string;
  accountId?: string;
  currentPage?: string;
}

export default function SupportWidget({ userId, accountId, currentPage }: SupportWidgetProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'bug' | 'ask'>('bug');

  // Bug report state
  const [bugMessage, setBugMessage] = useState('');
  const [bugLoading, setBugLoading] = useState(false);
  const [bugSuccess, setBugSuccess] = useState(false);
  const [bugError, setBugError] = useState<string | null>(null);

  // Ask support state
  const [askMessage, setAskMessage] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [askReply, setAskReply] = useState<string | null>(null);
  const [askError, setAskError] = useState<string | null>(null);

  async function submitBug() {
    if (!bugMessage.trim()) return;
    setBugLoading(true);
    setBugError(null);
    setBugSuccess(false);
    try {
      const res = await fetch(`${BASE_URL}/support/bug`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: bugMessage.trim(),
          page: currentPage || window.location.pathname,
          user_agent: navigator.userAgent,
          user_id: userId || null,
          account_id: accountId || null,
        }),
      });
      const envelope = await res.json();
      if (!envelope.ok) throw new Error(envelope.error?.message || 'Failed to submit bug report');
      setBugSuccess(true);
      setBugMessage('');
    } catch (err) {
      setBugError(err instanceof Error ? err.message : String(err));
    } finally {
      setBugLoading(false);
    }
  }

  async function submitAsk() {
    if (!askMessage.trim()) return;
    setAskLoading(true);
    setAskError(null);
    setAskReply(null);
    try {
      const res = await fetch(`${BASE_URL}/support/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: askMessage.trim(),
          user_id: userId || null,
        }),
      });
      const envelope = await res.json();
      if (!envelope.ok) throw new Error(envelope.error?.message || 'Failed to get support response');
      setAskReply(envelope.data?.reply || envelope.data?.message || 'Response received.');
    } catch (err) {
      setAskError(err instanceof Error ? err.message : String(err));
    } finally {
      setAskLoading(false);
    }
  }

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: '#059669',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(5,150,105,0.35)',
            zIndex: 9999,
            transition: 'transform 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          aria-label="Report a bug or contact support"
          title="Report a bug or contact support"
        >
          <Bug size={22} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 360,
            maxHeight: 520,
            background: 'var(--card-bg, #111118)',
            border: '1px solid var(--card-border, #1e1e2e)',
            borderRadius: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.85rem 1rem',
              borderBottom: '1px solid var(--card-border, #1e1e2e)',
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: 'var(--app-text, #e2e2e8)',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
            >
              Support
            </span>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--muted-text, #6b6b80)',
                cursor: 'pointer',
                padding: 4,
              }}
              aria-label="Close support"
            >
              <X size={16} />
            </button>
          </div>

          {/* Tabs */}
          <div
            style={{
              display: 'flex',
              gap: 0,
              margin: '0.75rem 1rem 0',
              background: 'var(--input-bg, #0d0d14)',
              borderRadius: 8,
              padding: 3,
            }}
          >
            {([
              { key: 'bug' as const, label: 'Report a Bug', icon: <Bug size={10} /> },
              { key: 'ask' as const, label: 'Ask Support', icon: <HelpCircle size={10} /> },
            ]).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                style={{
                  flex: 1,
                  padding: '0.4rem 0',
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: tab === t.key ? 'var(--card-bg, #111118)' : 'transparent',
                  color: tab === t.key ? 'var(--app-text, #e2e2e8)' : 'var(--muted-text, #6b6b80)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  boxShadow: tab === t.key ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{ padding: '1rem', flex: 1, overflowY: 'auto' }}>
            {tab === 'bug' ? (
              <>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.3rem',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'var(--muted-text, #6b6b80)',
                    letterSpacing: '0.05em',
                  }}
                >
                  Describe the issue
                </label>
                <textarea
                  value={bugMessage}
                  onChange={(e) => setBugMessage(e.target.value)}
                  placeholder="What went wrong? Steps to reproduce..."
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    background: 'var(--input-bg, #0d0d14)',
                    border: '1px solid var(--card-border, #1e1e2e)',
                    borderRadius: 8,
                    color: 'var(--app-text, #e2e2e8)',
                    fontSize: 12,
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ marginTop: '0.5rem', fontSize: 10, color: 'var(--muted-text, #6b6b80)' }}>
                  Page and browser info will be included automatically.
                </div>

                {bugError && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.4rem 0.6rem',
                      background: 'rgba(239,68,68,0.08)',
                      border: '1px solid rgba(239,68,68,0.2)',
                      borderRadius: 6,
                      color: '#ef4444',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {bugError}
                  </div>
                )}

                {bugSuccess && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.4rem 0.6rem',
                      background: 'rgba(5,150,105,0.08)',
                      border: '1px solid rgba(5,150,105,0.2)',
                      borderRadius: 6,
                      color: '#10b981',
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <CheckCircle size={12} />
                    Bug report submitted. Thank you!
                  </div>
                )}

                <button
                  onClick={submitBug}
                  disabled={bugLoading || !bugMessage.trim()}
                  style={{
                    marginTop: '0.75rem',
                    width: '100%',
                    padding: '0.55rem 0',
                    background: bugLoading ? '#047857' : '#059669',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    cursor: bugLoading || !bugMessage.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: !bugMessage.trim() ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  {bugLoading ? (
                    <>
                      <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={12} />
                      Submit Bug Report
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.3rem',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'var(--muted-text, #6b6b80)',
                    letterSpacing: '0.05em',
                  }}
                >
                  Your question
                </label>
                <textarea
                  value={askMessage}
                  onChange={(e) => setAskMessage(e.target.value)}
                  placeholder="How can we help you?"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    background: 'var(--input-bg, #0d0d14)',
                    border: '1px solid var(--card-border, #1e1e2e)',
                    borderRadius: 8,
                    color: 'var(--app-text, #e2e2e8)',
                    fontSize: 12,
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                />

                {askError && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.4rem 0.6rem',
                      background: 'rgba(239,68,68,0.08)',
                      border: '1px solid rgba(239,68,68,0.2)',
                      borderRadius: 6,
                      color: '#ef4444',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {askError}
                  </div>
                )}

                {askReply && (
                  <div
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: 'var(--input-bg, #0d0d14)',
                      border: '1px solid var(--card-border, #1e1e2e)',
                      borderRadius: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: 'var(--muted-text, #6b6b80)',
                        marginBottom: '0.3rem',
                      }}
                    >
                      Response
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--app-text, #e2e2e8)', lineHeight: 1.5 }}>
                      {askReply}
                    </div>
                  </div>
                )}

                <button
                  onClick={submitAsk}
                  disabled={askLoading || !askMessage.trim()}
                  style={{
                    marginTop: '0.75rem',
                    width: '100%',
                    padding: '0.55rem 0',
                    background: askLoading ? '#047857' : '#059669',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    cursor: askLoading || !askMessage.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: !askMessage.trim() ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  {askLoading ? (
                    <>
                      <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={12} />
                      Send Question
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
